import React from "react";
import { EventBus } from "@engine/EventBus.js";
import { WorldStore } from "@store/WorldStore.js";
import { ItemIcon } from "./ItemIcon.js";

/**
 * I15b — container loot transfer modal.
 *
 * Opened when the I15a dispatcher rule emits `ui:container_open`. Reads
 * the stashed floor drop by containerId; "Take All" emits
 * `inventory:give_item` per stack (the real inventory-accrual path
 * lands with the C-series character-inventory work), removes the floor
 * drop, and marks the container looted. "Close" leaves items in the
 * drop so the player can return — re-clicking the container re-opens
 * the modal.
 */
export function ContainerLootModal() {
  const [containerId, setContainerId] = React.useState<string | null>(null);
  const [items, setItems] = React.useState<readonly string[]>([]);

  React.useEffect(() => {
    const off = EventBus.on("ui:container_open", ({ containerId: id }) => {
      const drop = WorldStore.getFloorDrops().find((d) => d.dropId === id);
      if (!drop || drop.items.length === 0) return;
      setItems(drop.items);
      setContainerId(id);
    });
    return off;
  }, []);

  if (containerId === null) return null;

  const close = () => {
    setContainerId(null);
    setItems([]);
  };

  const takeAll = () => {
    for (const itemId of items) {
      EventBus.emit("inventory:give_item", { itemId, qty: 1 });
    }
    WorldStore.removeFloorDrop(containerId);
    WorldStore.markContainerLooted(containerId);
    close();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "auto",
        zIndex: 200,
      }}
      onClick={close}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420,
          maxWidth: "92vw",
          maxHeight: "80vh",
          overflowY: "auto",
          background: "#101420",
          color: "#e8e8e8",
          border: "1px solid #445",
          borderRadius: 4,
          boxShadow: "0 4px 24px rgba(0,0,0,0.7)",
          padding: "18px 22px",
          fontFamily: "monospace",
        }}
      >
        <div
          style={{
            fontSize: 14,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "#aab",
            borderBottom: "1px solid #334",
            paddingBottom: 10,
            marginBottom: 14,
          }}
        >
          Container
        </div>

        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {items.map((itemId, idx) => (
            <li
              key={`${itemId}-${String(idx)}`}
              style={{
                background: "#1a2030",
                border: "1px solid #334",
                borderRadius: 2,
                padding: "6px 12px",
                fontSize: 13,
                color: "#cce",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <ItemIcon itemId={itemId} name={itemId} size={20} />
              <span>{itemId}</span>
            </li>
          ))}
        </ul>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            marginTop: 16,
          }}
        >
          <button
            onClick={close}
            style={{
              background: "#222",
              color: "#aab",
              border: "1px solid #445",
              padding: "6px 14px",
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: 12,
              borderRadius: 2,
            }}
          >
            Close
          </button>
          <button
            onClick={takeAll}
            style={{
              background: "#2a3a5a",
              color: "#cce",
              border: "1px solid #557",
              padding: "6px 18px",
              cursor: "pointer",
              fontFamily: "monospace",
              fontSize: 12,
              borderRadius: 2,
            }}
          >
            Take All
          </button>
        </div>
      </div>
    </div>
  );
}
